import {
    DEFAULT_CONFIDENCE_THRESHOLD,
    adjustedConfidenceFromOwlvit,
    predictionToAnnotation
} from "@/utils/predictions";
import { useMedia } from "@/contexts/MediaContext";
import { useModel } from "@/contexts/ModelContext";
import {
    Prediction,
    StreamInferEventHandlers,
    StreamInferRequestBody
} from "@/utils/api/inference";
import {
    useCurrentWorkflow,
    useCurrentWorkspaceApiKey,
    useTextPromptMode,
    usePromptLabels,
    useCurrentMedia
} from "@/store";
import { useFlow } from "@/contexts/FlowContext";
import { useFps } from "@/contexts/FpsContext";
import { useComputedValues } from "@/contexts/ComputedValuesContext";
import { createBatchedStateSetter } from "@/utils/batchedQueue";
import useApi, { useAsyncBoundOperation } from "./useApi";
import { useCallback } from "react";
import {
    useInferenceQueue,
    useGetInferenceQueueItemById,
    useAddToInferenceQueue,
    useUpdateInferenceQueueItemStatus,
    useUpdateInferenceQueueItemProgress,
    useClearInferenceQueue,
    InferenceQueueItem,
    InferenceStatus
} from "@/store";
import { InferenceTypes } from "@functions/rapid/services/inference/stream/types";
import { useVideoSegmentation } from "@/utils/videoSegmentation";
import { MediaItemMetadata, MediaItem } from "@/types/media";
import { useMediaUtils } from "./useMediaUtils";
import { Annotation } from "@/types/annotation";
import { useFlowState } from "./useFlowState";
import { BuildSteps, Flows } from "@/types/flows";
import { ToolType, useSaveSharedEditHistory } from "./useSaveSharedEditHistory";
import { v4 as uuidv4 } from "uuid";

enum WorkflowOutputsKeys {
    MESSAGE = "message",
    PREDICTIONS = "predictions",
    VISUALIZATION = "visualization"
}

export type GetPredictionsForFileParams = {
    mediaItem: MediaItem;
    abortController?: AbortController;
    reviewModel?: boolean;
    onProgress?: (progress: number) => void;
    inferencePayload?: Partial<StreamInferRequestBody>;
    extractedFrameMeta?: { videoId: string; segment: number };
    inferenceType?: InferenceTypes;
    textPrompts?: string[];
    customPromptingData?: any[];
    isDraft?: boolean;
    batchFlushIntervalMs?: number;
    useInferenceQueue?: boolean;
};

export const useGetPredictions = () => {
    const { setFlowLocked } = useFlow();
    const { flow, step } = useFlowState();
    const { completedMediaItems } = useMedia();
    const {
        setPredictionsForFileAndFrameSegment,
        setAnnotationsForFileAndFrameId,
        clearPredictionsForFile,
        annotationsForFileAndFrameId,
        predictionsForFileAndFrameSegment
    } = useModel();
    const { currentPromptingData } = useComputedValues();
    const api = useApi();
    const textPromptMode = useTextPromptMode();
    const promptLabels = usePromptLabels();
    const currentMedia = useCurrentMedia();
    const { updateMediaItem } = useMediaUtils();
    // Inference queue state management via store hooks
    const getInferenceQueue = useInferenceQueue();
    const getInferenceQueueItemById = useGetInferenceQueueItemById;
    const addToInferenceQueue = useAddToInferenceQueue();
    const updateInferenceQueueItemStatus = useUpdateInferenceQueueItemStatus();
    const updateInferenceQueueItemProgress = useUpdateInferenceQueueItemProgress();
    const clearInferenceQueue = useClearInferenceQueue();
    const { fps } = useFps();
    const { segmentForTargetTime } = useVideoSegmentation(
        currentMedia?.file.metadata?.videoMetadata?.duration || 0,
        fps
    );
    const workflow = useCurrentWorkflow();
    const apiKey = useCurrentWorkspaceApiKey();
    const saveSharedEditHistory = useSaveSharedEditHistory();

    // simple helper to wait for the entire inference queue to complete
    const checkInferenceQueue = useCallback(async () => {
        const all = Promise.all(
            Array.from(getInferenceQueue.entries()).map(([key, item]) => item.promise)
        );

        const results = await all;
        return results;
    }, [getInferenceQueue]);
    const { isLoading: isLoadingInferenceQueue, error: errorInferenceQueue } =
        useAsyncBoundOperation(checkInferenceQueue, { autoInvoke: true });

    const clearQueue = useCallback(() => {
        // The clearInferenceQueue method now handles aborting controllers internally
        clearInferenceQueue();
    }, [clearInferenceQueue]);

    // Helper function to create isolated progress tracking for each getPredictionsForFile call
    const createProgressTracker = useCallback(
        ({ mockProgress }: { mockProgress: boolean } = { mockProgress: false }) => {
            let mockedProgress = 0;
            let mockedProgressInterval: NodeJS.Timeout | null = null;
            let progress = 0;

            const startProgressInterval = (onProgress: (progress: number) => void) => {
                mockedProgress = 0;

                if (!mockProgress) {
                    return;
                }

                mockedProgressInterval = setInterval(() => {
                    mockedProgress += 1;
                    onProgress?.(mockedProgress);
                    progress = mockedProgress;

                    // Cap at 25% and clear interval
                    if (mockedProgress >= 25) {
                        if (mockedProgressInterval) {
                            clearInterval(mockedProgressInterval);
                            mockedProgressInterval = null;
                        }
                    }
                }, 500);
            };

            const updateProgress = (
                updatedProgress: number,
                onProgress: (progress: number) => void
            ) => {
                const progressBuffer = 5;

                if (updatedProgress > mockedProgress + progressBuffer) {
                    if (mockedProgressInterval) {
                        clearInterval(mockedProgressInterval);
                        mockedProgressInterval = null;
                    }

                    onProgress?.(updatedProgress);
                    progress = updatedProgress;
                }
            };

            const cleanup = () => {
                if (mockedProgressInterval) {
                    clearInterval(mockedProgressInterval);
                    mockedProgressInterval = null;
                }
            };

            return { startProgressInterval, updateProgress, cleanup, progress };
        },
        []
    );

    const parseOwlvitPredictions = (predictions: any) => {
        return predictions.map((p: any) => ({
            x: p.x,
            y: p.y,
            width: p.width,
            height: p.height,
            confidence: adjustedConfidenceFromOwlvit(p.confidence),
            class: p.class,
            class_id: p.class_id,
            detection_id: p.detection_id,
            label: p.class // this is redundant. TODO: implement consistent prediction schema
        }));
    };

    const parseWorkflowPredictions = (workflowResults: any) => {
        const { outputs = [] } = workflowResults;

        const predictionsOutput = outputs.find((o: any) => o[WorkflowOutputsKeys.PREDICTIONS]);

        if (!predictionsOutput) {
            return [];
        }

        const predictions = predictionsOutput[WorkflowOutputsKeys.PREDICTIONS]?.predictions || [];

        return predictions.map((p: any) => ({
            x: p.x,
            y: p.y,
            width: p.width,
            height: p.height,
            confidence: p.confidence,
            class: p.class,
            class_id: p.class_id,
            detection_id: p.detection_id,
            label: `${p.class} (${Math.round(p.confidence * 100)}%)`
        }));
    };

    const parseSam3Predictions = (predictions: any) => {
        return predictions.map((prediction, index) => {
            let x: number, y: number, width: number, height: number;
            let masks: any = null;

            // Handle mask predictions (RLE format from SAM3)
            if (prediction.type === "mask" && prediction.masks) {
                masks = prediction.masks;

                const maskWidth = masks.size[1]; // COCO format: size = [height, width]
                const maskHeight = masks.size[0];

                const bounds = calculateRLEMaskBounds(masks.counts, maskWidth, maskHeight);

                if (bounds) {
                    x = (bounds.minX + bounds.maxX) / 2;
                    y = (bounds.minY + bounds.maxY) / 2;
                    width = bounds.maxX - bounds.minX;
                    height = bounds.maxY - bounds.minY;
                } else {
                    x = maskWidth / 2;
                    y = maskHeight / 2;
                    width = maskWidth;
                    height = maskHeight;
                }
            }
            // Handle polygon predictions (legacy/fallback)
            else if (prediction.vertices) {
                let minX = Infinity,
                    minY = Infinity,
                    maxX = -Infinity,
                    maxY = -Infinity;

                prediction.vertices.forEach((vertex: any) => {
                    vertex.forEach(({ x, y }: { x: number; y: number }) => {
                        minX = Math.min(minX, x);
                        minY = Math.min(minY, y);
                        maxX = Math.max(maxX, x);
                        maxY = Math.max(maxY, y);
                    });
                });

                x = (minX + maxX) / 2;
                y = (minY + maxY) / 2;
                width = maxX - minX;
                height = maxY - minY;
            } else {
                x = y = width = height = 0;
            }

            return {
                x,
                y,
                width,
                height,
                confidence: prediction.confidence,
                class: prediction.class,
                class_id: 0,
                detection_id: prediction.detection_id || uuidv4(),
                label: prediction.class,
                masks,
                type: prediction.type || "polygon"
            };
        });
    };

    // Calculate bounding box from RLE mask
    const calculateRLEMaskBounds = (
        rleString: string,
        width: number,
        height: number
    ): { minX: number; minY: number; maxX: number; maxY: number } | null => {
        let minX = width;
        let minY = height;
        let maxX = 0;
        let maxY = 0;
        let found = false;

        try {
            const counts: number[] = [];
            let p = 0;
            let m = 0;

            while (p < rleString.length) {
                let x = 0;
                let k = 0;
                let more = 1;

                while (more) {
                    const c = rleString.charCodeAt(p) - 48;
                    x |= (c & 0x1f) << (5 * k);
                    more = c & 0x20;
                    p++;
                    k++;
                    if (!more && c & 0x10) {
                        x |= -1 << (5 * k);
                    }
                }

                if (m > 2) {
                    x += counts[m - 2];
                }
                counts[m++] = x;
            }

            let idx = 0;
            let val = 0;

            for (const count of counts) {
                if (val === 1) {
                    for (let i = 0; i < count; i++) {
                        const col = Math.floor(idx / height);
                        const row = idx % height;
                        if (col < width && row < height) {
                            minX = Math.min(minX, col);
                            minY = Math.min(minY, row);
                            maxX = Math.max(maxX, col);
                            maxY = Math.max(maxY, row);
                            found = true;
                        }
                        idx++;
                    }
                } else {
                    idx += count;
                }
                val = 1 - val;
            }

            if (!found) {
                return null;
            }

            return { minX, minY, maxX, maxY };
        } catch (error) {
            console.error("Error calculating RLE mask bounds:", error);
            return null;
        }
    };

    // Generate predictions for a single file using streaming inference
    const getPredictionsForFile = useCallback(
        async ({
            mediaItem,
            abortController,
            reviewModel = false,
            onProgress,
            inferencePayload,
            extractedFrameMeta,
            inferenceType,
            textPrompts,
            customPromptingData,
            isDraft = false,
            batchFlushIntervalMs = 100,
            useInferenceQueue = true
        }: GetPredictionsForFileParams): Promise<void> => {
            const dimensions = mediaItem.metadata?.naturalDimensions;
            const file = mediaItem.file;
            const reviewMode = step === BuildSteps.REVIEW || reviewModel;
            const goStraightToAnnotations = textPromptMode && !reviewMode && flow === Flows.BUILD;

            if (!mediaItem.id) {
                throw new Error(`Skipping predictions for file ${file.name} - missing file_uuid`);
            }

            if (!textPrompts && promptLabels.size > 0) {
                textPrompts = Array.from(promptLabels.values()).map((label) => label.name);
            }

            if (!inferenceType) {
                inferenceType = textPromptMode ? InferenceTypes.SEG_PREVIEW : InferenceTypes.OWLVIT;
            }

            const requiresPromptingData = [
                InferenceTypes.OWLVIT,
                InferenceTypes.SEG_PREVIEW
            ].includes(inferenceType);

            const promptingDataToUse = customPromptingData || currentPromptingData;

            // Only makes sense to check for prompting data if we're not using the workflow inference.
            if (requiresPromptingData && promptingDataToUse.length === 0 && !textPrompts?.length) {
                console.warn(
                    `[useGetPredictions] Skipping predictions for file ${file.name} - no prompting data`,
                    {
                        requiresPromptingData,
                        customPromptingDataLength: customPromptingData?.length ?? 0,
                        currentPromptingDataLength: currentPromptingData?.length ?? 0,
                        textPromptsLength: textPrompts?.length ?? 0,
                        hasCustomPromptingData: !!customPromptingData,
                        inferenceType
                    }
                );
                return;
            }

            const requiresWorkflow = [
                InferenceTypes.WORKFLOW,
                InferenceTypes.WORKFLOW_PUBLIC
            ].includes(inferenceType);

            if (requiresWorkflow && !workflow?.id) {
                console.warn(
                    `[useGetPredictions] Skipping predictions for file ${file.name} - no workflow id`
                );
                return;
            }

            if (inferenceType === InferenceTypes.WORKFLOW && !apiKey) {
                console.warn(
                    `[useGetPredictions] Skipping predictions for file ${file.name} - no api key`
                );
                return;
            }

            // Create and manage inference queue item if requested
            let queueAbortController = abortController;
            let queueOnProgress = onProgress;

            if (useInferenceQueue && mediaItem.id) {
                // Create abort controller if not provided
                if (!queueAbortController) {
                    queueAbortController = new AbortController();
                }

                // Create queue-aware progress callback
                const originalOnProgress = onProgress;
                queueOnProgress = (progress: number) => {
                    updateInferenceQueueItemProgress(mediaItem.id!, progress);
                    originalOnProgress?.(progress);
                };

                // Create queue item
                const queueItem: InferenceQueueItem = {
                    id: mediaItem.id,
                    status: InferenceStatus.QUEUED,
                    promise: Promise.resolve(), // Will be updated below
                    abortController: queueAbortController,
                    progress: 0
                };

                // Add to queue
                addToInferenceQueue(queueItem);

                // Update status to inferring
                updateInferenceQueueItemStatus(mediaItem.id, InferenceStatus.INFERRING);
            }

            let hasSetDimensions = false;

            if (!textPrompts?.length && inferenceType === InferenceTypes.SEG_PREVIEW) {
                inferenceType = InferenceTypes.OWLVIT;
            }

            const { startTimeMilliseconds, endTimeMilliseconds } = inferencePayload || {};

            // zero indexed
            const startFrame = startTimeMilliseconds
                ? segmentForTargetTime(startTimeMilliseconds)
                : 0;
            const endFrame = endTimeMilliseconds
                ? segmentForTargetTime(endTimeMilliseconds)
                : mediaItem.metadata?.frame_count
                  ? mediaItem.metadata?.frame_count - 1
                  : 0;
            const expectedPredictionsCount = endFrame - startFrame + 1;

            const upload = mediaItem.uploadResizedResult || mediaItem.uploadResult;
            if (!upload?.filename) {
                throw new Error(`File ${file.name} not uploaded yet`);
            }

            // Prepare streaming request payload
            const resizeRatio = upload.resizeRatio || 1;
            const streamPayload: StreamInferRequestBody = {
                fps: fps,
                startTimeMilliseconds,
                endTimeMilliseconds,
                fileId: upload.filename,
                inferenceType: inferenceType
            };

            if (requiresWorkflow) {
                streamPayload.workflowId = workflow?.id!;

                if (!!apiKey) {
                    streamPayload.apiKey = apiKey;
                }
            } else if (inferenceType === InferenceTypes.SEG_PREVIEW) {
                streamPayload.prompts = textPrompts?.map((text) => ({ text })) || [];
            } else {
                streamPayload.prompts = promptingDataToUse || [];
            }

            // Create isolated progress tracker for this call
            const progressTracker = createProgressTracker({ mockProgress: true });
            if (queueOnProgress) {
                progressTracker.startProgressInterval(queueOnProgress);
            }

            const processedSegments = new Set<number>();

            // Create batched version of predictions setter to queue updates and flush at fixed intervals
            // This ensures no intermediate state updates are lost during high-frequency streaming
            const batchedSetPredictions = createBatchedStateSetter<
                any, // State type
                { fileId: string; segment: number; processedPredictions: any } // Update object type
            >({
                setter: goStraightToAnnotations
                    ? setAnnotationsForFileAndFrameId
                    : setPredictionsForFileAndFrameSegment,
                flushIntervalMs: batchFlushIntervalMs,
                // Custom preprocessQueue function to merge data objects directly
                preprocessQueue: (prevState, updateQueue) => {
                    // OPTIMIZATION: Merge data objects directly without calling updater functions
                    const result = { ...prevState };

                    // Apply each data update directly
                    for (const update of updateQueue) {
                        const { fileId, segment, processedPredictions } = update;
                        result[fileId] = {
                            ...(result[fileId] || {}),
                            [segment]: processedPredictions
                        };
                    }

                    // Update progress
                    // 1. Use mocked progress until we get stream events back
                    // 2. Once we get stream events back, use the actual progress when it is greater than the mocked progress

                    let framesWithPredictionResultsCount = 0;

                    if (extractedFrameMeta) {
                        if (
                            result[extractedFrameMeta.videoId]?.[extractedFrameMeta.segment] !==
                            undefined
                        ) {
                            framesWithPredictionResultsCount++;
                        }
                    } else {
                        for (let i = startFrame; i <= endFrame; i++) {
                            // empty arrays count as results
                            if (result[mediaItem.id]?.[i] !== undefined) {
                                framesWithPredictionResultsCount++;
                            }
                        }
                    }

                    const progress = Math.max(
                        // never dip below a previously reached progress
                        progressTracker.progress,
                        Math.min(
                            100, // cap at 100%
                            Math.floor(
                                (framesWithPredictionResultsCount / expectedPredictionsCount) * 100
                            )
                        )
                    );

                    if (queueOnProgress) {
                        progressTracker.updateProgress(progress, queueOnProgress);
                    }

                    return result;
                }
            });

            const sendSharedEditHistory = async ({
                parsedPredictions,
                resizeRatio,
                segment,
                inferredDimensions
            }: {
                parsedPredictions: Prediction[];
                resizeRatio: number;
                segment: number;
                inferredDimensions: { width: number; height: number };
            }) => {
                // Only save shared edit history for relevant inference types
                const inferenceToToolType: Record<InferenceTypes, ToolType | null> = {
                    [InferenceTypes.SEG_PREVIEW]: ToolType.RAPID_TEXT_PROMPT,
                    [InferenceTypes.OWLVIT]: ToolType.RAPID_BOX_PROMPTING,
                    [InferenceTypes.WORKFLOW]: null,
                    [InferenceTypes.WORKFLOW_PUBLIC]: null
                };
                const toolType = inferenceToToolType[inferenceType as InferenceTypes];
                if (toolType && textPrompts) {
                    const modelUsed =
                        inferenceType === InferenceTypes.SEG_PREVIEW ? "sam3" : "owlv2";

                    // Already resized to inferred dimensions
                    const boxes = parsedPredictions.map((p) => ({
                        id: p.detection_id,
                        type: p.type === "mask" ? "mask" : "box",
                        label: p.class,
                        x: p.x,
                        y: p.y,
                        width: p.width,
                        height: p.height,
                        confidence: p.confidence,
                        labeledByModel: modelUsed,
                        mask: p.type === "mask" ? p.masks?.counts : undefined
                    }));

                    const boxIds = boxes.map((b) => b.id);
                    const resultingAnnotations = {
                        key: mediaItem.isVideo ? `${mediaItem.id}/${segment}` : mediaItem.id,
                        width: inferredDimensions.width,
                        height: inferredDimensions.height,
                        boxes
                    };
                    const extraData = {
                        annotations: resultingAnnotations,
                        tool_data: {
                            tool_type: toolType,
                            model_used: modelUsed,
                            text_prompts: textPrompts!.map((text) => ({
                                text,
                                confidence_threshold:
                                    promptLabels.get(text)?.confidenceThreshold ||
                                    DEFAULT_CONFIDENCE_THRESHOLD
                            })),
                            annotation_ids: boxIds, // ids that were created/modified by this tool
                            training_images_count:
                                modelUsed === "owlv2" ? promptingDataToUse.length : undefined,
                            prompt_boxes:
                                modelUsed === "owlv2"
                                    ? promptingDataToUse
                                          .map((image) =>
                                              image.boxes.map((box) => ({
                                                  image_id: image.file_uuid.split("/")[1],
                                                  id: box.id,
                                                  cls: box.cls,
                                                  x: box.x * resizeRatio,
                                                  y: box.y * resizeRatio,
                                                  w: box.w * resizeRatio,
                                                  h: box.h * resizeRatio,
                                                  confidence: box.confidence || 1,
                                                  negative: box.negative
                                              }))
                                          )
                                          .flat()
                                    : undefined
                        }
                    };
                    return saveSharedEditHistory(mediaItem, extraData);
                }
            };

            const onResult = async (data: any) => {
                if (abortController?.signal?.aborted) {
                    return;
                }

                const prediction = data.result;
                const inferenceItemId = data.id;

                let fileId: string;
                let segment: number;

                if (extractedFrameMeta) {
                    fileId = extractedFrameMeta.videoId;
                    segment = extractedFrameMeta.segment;
                } else {
                    // image ex: anonymous/1234-5678-9012-3456-7890
                    // image resized ex: anonymous/1234-5678-9012-3456-7890/size_max1008
                    // video ex: anonymous/1234-5678-9012-3456-7890/72
                    const splits = inferenceItemId.split("/");
                    fileId = splits[1];

                    const potentialSegment = parseInt(splits[2], 10);
                    if (!isNaN(potentialSegment)) {
                        segment = potentialSegment;
                    } else {
                        segment = 0;
                    }
                }
                processedSegments.add(segment);

                let inferredDimensions;

                if (
                    inferenceType === InferenceTypes.WORKFLOW ||
                    inferenceType === InferenceTypes.WORKFLOW_PUBLIC
                ) {
                    inferredDimensions = prediction.outputs?.[0]?.predictions?.image;
                } else {
                    inferredDimensions =
                        prediction.image ||
                        // seg preview doesn't return inferred image dimensions, so we use the original dimensions * resize ratio for now -- TODO: handle this better
                        (dimensions
                            ? {
                                  width: dimensions.width * resizeRatio,
                                  height: dimensions.height * resizeRatio
                              }
                            : null);
                }

                // Set inferred dimensions from the first prediction (only once)
                if (
                    !hasSetDimensions &&
                    inferredDimensions &&
                    inferredDimensions.width &&
                    inferredDimensions.height
                ) {
                    const newMetadata: MediaItemMetadata = {
                        ...mediaItem.metadata,
                        frame_count: mediaItem.metadata?.frame_count!, // this is to comply with a typescript requirement. frame_count is required. we will always have it here.
                        inferredDimensions: {
                            width: inferredDimensions.width / resizeRatio,
                            height: inferredDimensions.height / resizeRatio
                        }
                    };

                    // force currentMedia re renders after updating metadata
                    updateMediaItem({ ...mediaItem, metadata: newMetadata });
                    hasSetDimensions = true;
                }

                let parsedPredictions: Prediction[] = [];

                if (inferenceType === InferenceTypes.OWLVIT) {
                    parsedPredictions = parseOwlvitPredictions(prediction.predictions);
                } else if (inferenceType === InferenceTypes.SEG_PREVIEW) {
                    parsedPredictions = parseSam3Predictions(prediction);
                } else if (
                    [InferenceTypes.WORKFLOW, InferenceTypes.WORKFLOW_PUBLIC].includes(
                        inferenceType
                    )
                ) {
                    parsedPredictions = parseWorkflowPredictions(prediction);
                }

                let processedPredictions: Prediction[] | Annotation[] = parsedPredictions.map(
                    (p: any) => ({
                        ...p,
                        fileId: fileId,
                        frameId: mediaItem.id,
                        frameSegment: segment,
                        // resize predictions back to original image size if needed
                        width: p.width / resizeRatio,
                        height: p.height / resizeRatio,
                        x: p.x / resizeRatio,
                        y: p.y / resizeRatio,
                        // mark as draft
                        draft: isDraft
                    })
                );
                if (goStraightToAnnotations) {
                    processedPredictions = processedPredictions.map((p) =>
                        predictionToAnnotation(p as Prediction, fileId)
                    );
                }

                batchedSetPredictions({
                    fileId,
                    segment: goStraightToAnnotations ? mediaItem.id : segment,
                    processedPredictions
                });

                try {
                    sendSharedEditHistory({
                        parsedPredictions,
                        resizeRatio,
                        segment,
                        inferredDimensions
                    });
                } catch (error: any) {
                    // Swallow errors
                }
            };

            const eventHandlers: StreamInferEventHandlers = {
                onStarted: (data) => {},

                onResult,

                onComplete: (data) => {
                    // Flush any remaining batched updates immediately
                    batchedSetPredictions.flush();
                    if (queueOnProgress) {
                        progressTracker.updateProgress(100, queueOnProgress);
                    }
                    progressTracker.cleanup();

                    // Ugly hack to update queue status to completed AFTER predictions are flushed to store
                    // Use setTimeout to ensure predictions propagate through React's render cycle first
                    // required so that UIs PromptAnnotateDataSidebarContent and PromptSidebarContent update in sync
                    if (useInferenceQueue && mediaItem.id) {
                        setTimeout(() => {
                            updateInferenceQueueItemStatus(mediaItem.id, InferenceStatus.COMPLETED);
                            updateInferenceQueueItemProgress(mediaItem.id, 100);
                        }, 0);
                    }
                },

                onError: (data) => {
                    console.error("Streaming inference error:", data);
                    // Cancel any pending batched updates on error
                    batchedSetPredictions.cancel();
                    progressTracker.cleanup();

                    // Update queue status to error
                    if (useInferenceQueue && mediaItem.id) {
                        const error =
                            data instanceof Error ? data : new Error(data?.message || String(data));
                        updateInferenceQueueItemStatus(mediaItem.id, InferenceStatus.ERROR, error);
                    }
                }
            };

            try {
                await api.inference.streamInfer(streamPayload, eventHandlers);
            } catch (error: any) {
                api.inference.abortStreamInfer(streamPayload.fileId, streamPayload.fps);
                // Cancel any pending batched updates on error
                batchedSetPredictions.cancel();
                progressTracker.cleanup();

                // Update queue status to error
                if (useInferenceQueue && mediaItem.id && error.name !== "AbortError") {
                    updateInferenceQueueItemStatus(mediaItem.id, InferenceStatus.ERROR, error);
                }

                // Always throw the error so the caller knows the operation failed
                throw error;
            }
        },
        [
            currentPromptingData,
            setPredictionsForFileAndFrameSegment,
            api.inference,
            createProgressTracker,
            segmentForTargetTime,
            addToInferenceQueue,
            updateInferenceQueueItemStatus,
            updateInferenceQueueItemProgress,
            workflow,
            apiKey,
            promptLabels,
            textPromptMode,
            updateMediaItem,
            setAnnotationsForFileAndFrameId,
            flow,
            fps,
            step,
            saveSharedEditHistory,
            annotationsForFileAndFrameId,
            predictionsForFileAndFrameSegment
        ]
    );

    const generateModel = useCallback(async () => {
        // Main function to generate predictions for all image files using combined training data
        if (currentPromptingData.length === 0) {
            throw new Error("No annotations found. Please annotate some files first.");
        }

        if (completedMediaItems.length === 0) {
            throw new Error("Please wait for files to upload before generating a model.");
        }

        // Set loading state
        setFlowLocked(true);

        try {
            // Clear existing predictions and queue for all files
            completedMediaItems.forEach((item) => {
                clearPredictionsForFile(item.id);
            });
            clearQueue();

            // Generate predictions for all files - queue management is now handled internally
            let queuedCount = 0;

            // Start inference for all files (getPredictionsForFile now handles queue management internally)
            completedMediaItems.forEach((mediaItem) => {
                if (!mediaItem.id) return;

                queuedCount++;

                // Fire and forget - getPredictionsForFile handles its own queue management
                getPredictionsForFile({
                    mediaItem,
                    useInferenceQueue: true, // Explicitly enable queue management
                    reviewModel: true
                }).catch((error: any) => {
                    if (error.name === "AbortError") {
                        return;
                    }
                    console.error(
                        `ModelGeneration: Error generating predictions for ${mediaItem.file.name}:`,
                        error
                    );
                });
            });

            // Return success immediately after starting all inferences
            // The queue management in getPredictionsForFile will handle the rest
            return { success: true, queuedCount };
        } catch (error: any) {
            console.error("[useGetPredictions] error", error);
            throw error;
        } finally {
            setFlowLocked(false);
        }
    }, [
        setFlowLocked,
        currentPromptingData,
        completedMediaItems,
        clearPredictionsForFile,
        getPredictionsForFile,
        clearQueue
    ]);

    return {
        generateModel,
        getPredictionsForFile,

        // Inference queue-related functions
        inferenceQueue: getInferenceQueue,
        getInferenceQueueItemById,
        updateInferenceQueueItemProgress,
        clearInferenceQueue: clearQueue,
        isLoadingInferenceQueue,
        errorInferenceQueue
    };
};
